// ControlObligationsTab — lists compliance framework mappings for the current control.
// Data comes from GET /controls/{control_id}/frameworks.
// Each row links to the obligation detail page.

import { useParams, useNavigate } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { useApi } from '@/lib/api/client'
import { useTenant } from '@/components/shared/TenantShell'
import { controlsApi, type ComplianceMappingSummary } from '@/lib/api/controls'
import { queryKeys } from '@/lib/api/queryKeys'
import { ErrorState } from '@/components/shared/ErrorState'
import { TableSkeleton } from '@/components/shared/LoadingSkeleton'
import { Badge } from '@/components/ui/badge'
import { Scale, FileText, Calendar, ExternalLink } from 'lucide-react'
import { cn } from '@/lib/utils'

const coverageLevelLabels: Record<string, string> = {
  full: 'Full',
  partial: 'Partial',
  planned: 'Planned',
}

const coverageLevelColors: Record<string, string> = {
  full: 'bg-green-100 text-green-700',
  partial: 'bg-amber-100 text-amber-700',
  planned: 'bg-blue-100 text-blue-700',
}

export function ControlObligationsTab() {
  const { slug } = useTenant()
  const { controlId } = useParams({ strict: false }) as { controlId: string }
  const navigate = useNavigate()
  const api = useApi()
  const ctrlApi = controlsApi(api)

  const {
    data: mappings,
    error,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: queryKeys.controls.frameworks(slug, controlId),
    queryFn: () => ctrlApi.frameworks(controlId),
  })

  if (isLoading) return <TableSkeleton rows={4} />

  if (error) {
    return (
      <div className="p-6">
        <ErrorState
          message="Failed to load obligations"
          technicalDetail={error.message}
          onRetry={() => refetch()}
        />
      </div>
    )
  }

  if (!mappings || mappings.length === 0) {
    return (
      <div className="flex h-48 items-center justify-center text-muted-foreground">
        <div className="space-y-2 text-center">
          <Scale className="mx-auto h-10 w-10 text-muted-foreground/40" />
          <p className="text-sm">No obligations are mapped to this control.</p>
          <p className="text-xs text-muted-foreground/60">
            Obligations can be mapped from the Obligations section.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6">
      <div className="mb-3 text-sm text-muted-foreground">
        {mappings.length} mapped obligation{mappings.length !== 1 ? 's' : ''}
      </div>
      <div className="divide-y rounded-lg border">
        {mappings.map((mapping) => (
          <MappingRow
            key={mapping.id}
            mapping={mapping}
            onClick={() => navigate({ to: `/t/${slug}/obligations/${mapping.framework_id}` })}
          />
        ))}
      </div>
    </div>
  )
}

function MappingRow({
  mapping,
  onClick,
}: {
  mapping: ComplianceMappingSummary
  onClick: () => void
}) {
  return (
    <div
      className="group flex cursor-pointer items-center gap-4 px-4 py-3 transition-colors hover:bg-accent/30"
      onClick={onClick}
    >
      {/* Reference code + title */}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-muted-foreground">
            {mapping.reference_code}
          </span>
          <span className="truncate text-sm">{mapping.title}</span>
          <ExternalLink className="h-3 w-3 flex-shrink-0 text-muted-foreground opacity-0 group-hover:opacity-100" />
        </div>
        {mapping.requirement_group && (
          <div className="mt-0.5 truncate text-xs text-muted-foreground">
            {mapping.requirement_group}
          </div>
        )}
        {mapping.tenant_interpretation && (
          <div className="mt-1 text-xs italic text-muted-foreground">
            {mapping.tenant_interpretation}
          </div>
        )}
      </div>

      {/* Framework name */}
      <div className="hidden items-center gap-1 text-xs text-muted-foreground md:flex">
        <FileText className="h-3 w-3" />
        <span className="max-w-[140px] truncate">{mapping.framework_name}</span>
      </div>

      {/* Mapped date */}
      <div className="hidden items-center gap-1 text-xs text-muted-foreground xl:flex">
        <Calendar className="h-3 w-3" />
        <span>{new Date(mapping.mapped_at).toLocaleDateString()}</span>
      </div>

      {/* Coverage badge */}
      <Badge
        variant="outline"
        className={cn(
          'flex-shrink-0 px-1.5 py-0 text-xs',
          coverageLevelColors[mapping.coverage_level],
        )}
      >
        {coverageLevelLabels[mapping.coverage_level] ?? mapping.coverage_level}
      </Badge>
    </div>
  )
}
